import { capture } from "@/lib/tracking/capture";
import { getTenantInfo, getDatastores, getCompanyInfo } from "@/helpers/apis";
import { updateTenantInfo, updateDatastores, setCompanyInfo } from "./appSlice";
import { Dispatch, useState } from "react";
import { UnknownAction } from "@reduxjs/toolkit";
import { useAppState } from "./useAppContext";
import { getTenant } from "@/helpers/tenant";

const useAppActions = (dispatch: Dispatch<UnknownAction>) => {
  const { userGroup, loggedinUser } = useAppState();

  const [loadingTenantInfo, setLoadingTenantInfo] = useState(true);
  const [loadingDatastores, setLoadingDatastores] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const fetchTenantInfo = () => {
    return new Promise<void>((resolve, reject) => {
      setLoadingTenantInfo(true);
      getTenantInfo()
        .then(response => {
          capture("tenant_info", { response });
          if (response) {
            dispatch(updateTenantInfo(response));
          }
          resolve();
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          setLoadingTenantInfo(false);
        });
    });
  };

  const fetchDatastores = () => {
    return new Promise<void>((resolve, reject) => {
      setLoadingDatastores(true);
      getDatastores()
        .then(response => {
          dispatch(updateDatastores(response?.data_stores));
          resolve();
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          setLoadingDatastores(false);
        });
    });
  };

  const fetchCompanies = () => {
    return new Promise<void>(resolve => {
      setLoadingCompanies(true);
      getCompanyInfo()
        .then(response => {
          if (response) {
            dispatch(setCompanyInfo(response));
          }
          resolve();
        })
        .catch(err => {
          console.error("Failed to fetch company info:", err);
          resolve(); // Don't reject, just log the error
        })
        .finally(() => {
          setLoadingCompanies(false);
        });
    });
  };

  const getCommonHeaders = () => {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${loggedinUser?.auth_token || ""}`,
      "X-Tenant": getTenant(),
    };

    if (userGroup) {
      headers["X-User-Group-Id"] = userGroup.id;
    }

    return headers;
  };

  return {
    fetchTenantInfo,
    fetchDatastores,
    fetchCompanies,
    loadingTenantInfo,
    loadingDatastores,
    loadingCompanies,
    setLoadingTenantInfo,
    setLoadingDatastores,
    setLoadingCompanies,
    getCommonHeaders,
  };
};

export default useAppActions;
